/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { TCategoryChoice, TClientData, TPageNotification, TUser } from '../../../../types';
import { executeHttpRequest } from '../../../../epics/api';
import { getErrorMessages } from '../../../../utils';
import ClientAssignmentDetails from '../../../common/client/clientAssignmentDetails';

type TClientFields = {
    company_name: string;
    first_name: string;
    last_name: string;
    category: string;
    mobile: string;
    legal_name: string;
    billing_email: string;
    billing_address_1: string;
    billing_address_2: string;
    billing_city: string;
    billing_postcode: string;
}

type TEditProfileProps = {
    notification: TPageNotification;
    user: TUser;
}

export default function EditProfile({
    notification,
    user,
}: TEditProfileProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clientData, setClientData] = useState<TClientData>(user?.client);

    const [fields, setFields] = useState<TClientFields>({
        company_name: clientData.company_name,
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        category: clientData.category,
        mobile: clientData.mobile,
        legal_name: clientData.legal_name,
        billing_email: clientData.billing_email,
        billing_address_1: clientData.billing_address_1,
        billing_address_2: clientData.billing_address_2,
        billing_city: clientData.billing_city,
        billing_postcode: clientData.billing_postcode,
    });
    const [clientCategoryOptions, setClientCategoryOptions] = useState([]);

    const fetchClientCategories = () => executeHttpRequest('client-categories/')
        .then(res => {
            const categoryChoices = res.data.content.map(
                (choice: TCategoryChoice) => ({
                    value: choice.id,
                    label: choice.category,
                })
            );
            if (categoryChoices) {
                setClientCategoryOptions(categoryChoices);
            }
        });

    useEffect(() => {
        fetchClientCategories();
    }, []);

    const handleSaveButtonClick = () => {
        setIsLoading(true);
        setError(null);

        const payload = new FormData();
        Object.entries(fields)
            .filter(([key, value]: [keyof TClientFields, string]) => value !== clientData[key])
            .forEach(([key, value]) => payload.append(key, value));

        executeHttpRequest(`clients/${clientData.id}/`, 'patch', payload)
            .then(response => {
                setIsLoading(false);
                setIsEditing(false);
                setClientData(response.data.content);
                notification.success('Success', 'Profile updated successfully!');
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const handleCancelButtonClick = () => {
        setIsEditing(false);
        setError(null);
        // Reset fields back to original clientData values
        setFields({
            company_name: clientData.company_name,
            first_name: clientData.first_name,
            last_name: clientData.last_name,
            category: clientData.category,
            mobile: clientData.mobile,
            legal_name: clientData.legal_name,
            billing_email: clientData.billing_email,
            billing_address_1: clientData.billing_address_1,
            billing_address_2: clientData.billing_address_2,
            billing_city: clientData.billing_city,
            billing_postcode: clientData.billing_postcode,
        });
    };

    const handleEditButtonClick = () => setIsEditing(true);

    const handleLogoChange = (e: any) => {
        const payload = new FormData();
        payload.append('logo', e.target.files[0]);

        executeHttpRequest(`clients/${clientData.id}/`, 'patch', payload)
            .then(response => {
                setClientData(response.data.content);
                notification.success('Success', 'Logo updated successfully!');
            })
            .catch(err => {
                setError(getErrorMessages(err));
            });
    };

    const handleTextFieldChange = (e: any) => {
        setFields({
            ...fields,
            [e.target.name]: e.target.value,
        });
    };

    const handleSelectChange = (e: any, fieldName: 'category') => {
        setFields({
            ...fields,
            [fieldName]: e.value,
        });
    };

    const renderSectionButtons = () => (
        isEditing ? (
            <div className="freelancer-details-section__btns">
                <button
                    type="button"
                    disabled={isLoading}
                    className="freelancer-details-section__btn"
                    onClick={handleSaveButtonClick}
                >
                    Save
                </button>
                <button
                    data-testid="cancelBtn"
                    type="button"
                    disabled={isLoading}
                    className="freelancer-details-section__btn"
                    onClick={handleCancelButtonClick}
                >
                    Cancel
                </button>
            </div>
        ) : (
            <div className="freelancer-details-section__btns">
                <button
                    data-testid="editBtn"
                    type="button"
                    className="freelancer-details-section__btn"
                    onClick={handleEditButtonClick}
                >
                    <i className="icon icon-pencil-3"/>
                    Edit
                </button>
            </div>
        )
    );

    const renderTextFieldRow = (
        fieldName: keyof TClientFields,
        humanReadable: string,
        comment?: string,
    ) => (
        <tr>
            <td className="table-default__cell--text-blue">
                <span>{humanReadable}</span>
                {isEditing && comment && (
                    <>
                        <br/>
                        <span className="edit-profile-comment">{comment}</span>
                    </>
                )}
            </td>
            <td>
                {isEditing ? (
                    <input
                        type="text"
                        name={fieldName}
                        value={fields[fieldName]}
                        onChange={handleTextFieldChange}
                    />
                ) : (
                    clientData[fieldName]
                )}
            </td>
        </tr>
    );

    return (
        <div className="freelancer-details-container">
            <div className="freelancer-details-header" style={{ marginTop: '100px' }}>
                <div className="freelancer-details-header__avatar">
                    <div className="freelancer-avatar">
                        <div className="freelancer-avatar__photo">
                            <img src={clientData.logo} alt="freelancer"/>
                            {user.isClient && (
                                <label className="freelancer-avatar__edit-btn">
                                    <i className="icon icon-pencil-3"/>
                                    <input
                                        className="freelancer-avatar__file-input"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                </label>
                            )}
                        </div>
                        <div className="freelancer-avatar__name">{clientData.company_name}</div>
                    </div>
                </div>
            </div>
            <div className="freelancer-details-section">
                <div className="freelancer-details-section__header">
                    <div className="freelancer-details-section__title">About your business</div>
                    {renderSectionButtons()}
                </div>
                <div>
                    {error &&
                        <div className="freelancer-details-section__errors">
                            <p>{error}</p>
                        </div>
                    }
                </div>
                <div className="freelancer-details-section__content">
                    <table className="table-default">
                        <tbody>
                        {renderTextFieldRow('company_name', 'Company name', 'The trading name of your company')}
                        <tr>
                            <td className="table-default__cell--text-blue">
                                <span>Name</span>
                                {isEditing && (
                                    <>
                                        <br/>
                                        <span className="edit-profile-comment">First name and last name</span>
                                    </>
                                )}
                            </td>
                            {isEditing ? (
                                <td>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={fields.first_name}
                                        onChange={handleTextFieldChange}
                                    />
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={fields.last_name}
                                        onChange={handleTextFieldChange}
                                    />
                                </td>
                            ) : (
                                <td>{clientData.first_name} {clientData.last_name}</td>
                            )}
                        </tr>
                        <tr>
                            <td className="table-default__cell--text-blue">Category of your business</td>
                            {isEditing ? (
                                <td>
                                    <Select
                                        // @ts-ignore
                                        value={fields.category}
                                        clearable={false}
                                        onChange={(e: any) => handleSelectChange(e, 'category')}
                                        options={clientCategoryOptions}
                                    />
                                </td>
                            ) : (
                                <td>{clientCategoryOptions.find(cat => cat.value === clientData.category)?.label}</td>
                            )}
                        </tr>
                        {renderTextFieldRow('mobile', 'Mobile', 'Or landline number')}
                        </tbody>
                    </table>
                </div>
                <div className="freelancer-details-section__header">
                    <div className="freelancer-details-section__subtitle">Billing contact details</div>
                </div>
                <div className="freelancer-details-section__content">
                    <table className="table-default">
                        <tbody>
                            {renderTextFieldRow('legal_name', 'Legal name', 'The legal name of your company')}
                            <tr>
                                <td className="table-default__cell--text-blue">
                                    <span>E-mail addresses</span>
                                    {isEditing && (
                                        <>
                                            <br/>
                                            <span className="edit-profile-comment">
                                                Invoices will be sent to these email addresses, one per line
                                            </span>
                                        </>
                                    )}
                                </td>
                                {isEditing ? (
                                    <td>
                                        <textarea
                                            name="billing_email"
                                            value={fields.billing_email}
                                            rows={3}
                                            onChange={handleTextFieldChange}
                                        />
                                    </td>
                                ) : (
                                    <td>{clientData.billing_email}</td>
                                )}
                            </tr>
                            {renderTextFieldRow('billing_address_1', 'Address line 1')}
                            {renderTextFieldRow('billing_address_2', 'Address line 2')}
                            {renderTextFieldRow('billing_city', 'City')}
                            {renderTextFieldRow('billing_postcode', 'Postcode')}
                        </tbody>
                    </table>
                </div>
            </div>
            <ClientAssignmentDetails
                clientId={clientData.id}
                clientData={clientData}
                notification={notification}
                setClientData={setClientData}
                user={user}
            />
        </div>
    );
}
